import React, { useMemo } from 'react';
import { FormInstance } from 'antd/lib/form';
import { Popconfirm, Form, Space, Col } from 'antd';
import AddButton from 'components/AddButtonSmall';
import DeleteButton from 'components/DeleteButton';
import { PlusOutlined } from '@ant-design/icons';
import { sumBy } from 'lodash';
import { toCurrency, toRate } from 'utils/utils';
import { shouldUpdateCompare } from 'utils/utils2';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { AssetComponent } from '../../../services/assets';
import { useFormList } from '../../../hooks/useFormList';
import ParListSelect from 'components/ParListSelect';
import TaxSelect from 'components/TaxSelect';
import Value from 'components/Value';
import DF from 'DisplayFramework';
import ModalControllerNV from 'components/ModalControllerNV';
import AssetCatalogComponentsTableModal from './AssetCatalogComponentsTableModal';
import FormInput from 'components/FormInput';
import Title from 'components/Title';

const NEW_ASSET_CATALOG_COMPONENT_ID = -1;
type AssetCatalogComponentsTableProps = {
  form: FormInstance;
  currcode: string;
  showAsModal?: boolean;
  iscreate?: boolean;
};

const AssetCatalogComponentsTable = (
  props: AssetCatalogComponentsTableProps
) => {
  const { form, showAsModal, iscreate } = props;
  const fieldName = 'assetCatalogComponents';

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'compid',
  });

  // Prevent from crashing if the field is not set up
  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: [],
    });
  }

  const addRow = (item: any) => {
    add(item);
    const values = form.getFieldValue(fieldName) as AssetComponent[];
    const newValues = values.map((elem, i) => {
      if (item.compid !== elem.compid) {
        return elem;
      }
      const updatedItem = { ...elem, ...item };

      return {
        ...updatedItem,
      };
    });

    let assetunitnetamount = sumBy(newValues, 'compnetamount');
    let assetqty =
      form.getFieldValue('assetqty') !== undefined
        ? form.getFieldValue('assetqty')
        : 1;
    let assetnetamount = assetunitnetamount * assetqty;
    let assetunittaxamount = sumBy(newValues, 'comptaxamount');
    let assettaxamount = sumBy(newValues, 'comptaxamount') * assetqty;
    let assetunitgrossamount = sumBy(newValues, 'compgrossamount');
    let assetgrossamount = sumBy(newValues, 'compgrossamount') * assetqty;
    form.setFieldsValue({
      [fieldName]: newValues,
      assetqty: assetqty,
      assetunitnetamount,
      assetunittaxamount,
      assetunitgrossamount,
      assetnetamount,
      assettaxamount,
      assetgrossamount,
    });
  };

  // const recalculate = useCallback(
  //   (index: number, updatedValues: any) => {
  //     const values = form.getFieldValue(fieldName) as AssetComponent[];

  //     const newValues = values.map((item, i) => {
  //       if (i !== index) {
  //         return item;
  //       }

  //       const updatedItem = { ...item, ...updatedValues };
  //       const comporiginalnetamount = updatedItem.comporiginalnetamount;
  //       const compnetamount =
  //         updatedItem.comporiginalnetamount *
  //         (1 - updatedItem.compdiscountrate / 100);

  //       const comptaxamount = compnetamount * (updatedItem.taxrate / 100);

  //       const compgrossamount = compnetamount + comptaxamount;
  //       const discountamount = comporiginalnetamount - compnetamount;

  //       return {
  //         ...updatedItem,
  //         discountamount,
  //         comporiginalnetamount,
  //         compnetamount,
  //         comptaxamount,
  //         compgrossamount,
  //       };
  //     });
  //     //const assetdiscountperc=sumBy(newValues,'compdiscountrate')
  //     const assetdiscountamount = sumBy(newValues, 'discountamount');
  //     const assetnetamountorigine = sumBy(newValues, 'comporiginalnetamount');
  //     const assetnetamount = sumBy(newValues, 'compnetamount');
  //     const assettaxamount = sumBy(newValues, 'comptaxamount');
  //     const assetgrossamount = sumBy(newValues, 'compgrossamount');

  //     form.setFieldsValue({
  //       [fieldName]: newValues,
  //       //assetdiscountperc,
  //       assetdiscountamount,
  //       assetnetamountorigine,
  //       assetnetamount,
  //       assettaxamount,
  //       assetgrossamount,
  //     });
  //   },
  //   [form]
  // );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const update = (item: AssetComponent) => {
    const values = form.getFieldValue(fieldName) as AssetComponent[];

    const newValues = values.map((elem, i) => {
      if (item.compid !== elem.compid) {
        return elem;
      }
      const updatedItem = { ...elem, ...item };

      return {
        ...updatedItem,
      };
    });
    let assetunitnetamount = sumBy(newValues, 'compnetamount');
    let assetqty =
      form.getFieldValue('assetqty') !== undefined
        ? form.getFieldValue('assetqty')
        : 1;
    let assetnetamount = assetunitnetamount * assetqty;
    let assetunittaxamount = sumBy(newValues, 'comptaxamount');
    let assettaxamount = sumBy(newValues, 'comptaxamount') * assetqty;
    let assetunitgrossamount = sumBy(newValues, 'compgrossamount');
    let assetgrossamount = sumBy(newValues, 'compgrossamount') * assetqty;
    form.setFieldsValue({
      [fieldName]: newValues,
      assetqty: assetqty,
      assetunitnetamount,
      assetunittaxamount,
      assetunitgrossamount,
      assetnetamount,
      assettaxamount,
      assetgrossamount,
    });
  };

  const columns = useMemo<DataGridColDef<AssetComponent>[]>(
    () => [
      {
        comcode: 'comptype',
        key: 'comptype',
        width: '8%',
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'comptype']}>
            <ParListSelect parcode="COMPTYPE" disabled />
          </Form.Item>
        ),
        fixed: 'left',
      },
      {
        comcode: 'compcategory',
        key: 'compcategory',
        width: '10%',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate={() => true}>
            {({ getFieldValue }) => (
              <Form.Item name={[fieldName, index, 'compcategory']}>
                <ParListSelect
                  parcode="COMPCAT"
                  parvalueparent={getFieldValue([fieldName, index, 'comptype'])}
                  disabled
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'compdescription',
        key: 'compdescription',
        width: '15%',
        render: (_value, _record, index) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <Form.Item shouldUpdate={shouldUpdateCompare([fieldName])}>
                {({ getFieldValue }) => {
                  return (
                    <FormInput
                      col
                      xs={24}
                      name={[fieldName, index, 'compdescription']}
                      noStyle
                    >
                      <a onClick={() => setVisible(true)}>
                        {getFieldValue([fieldName, index, 'compdescription'])}
                      </a>
                    </FormInput>
                  );
                }}
              </Form.Item>
            )}
            modal={({ setVisible, visible }) => (
              <AssetCatalogComponentsTableModal
                onCancel={() => {
                  setVisible(false);
                }}
                onOk={(item) => {
                  setVisible(false);
                  update(item);
                }}
                visible={visible}
                addorupdate="UPDATE"
                currcode={form.getFieldValue('currcode')}
                formC={form}
                compid={_record.compid}
                record={_record}
              />
            )}
          />
        ),
        // render: (_value, _record, index) => (
        //   <Form.Item name={[fieldName, index, 'compdescription']} rules={[{ required: true }]}>
        //     <Input />
        //   </Form.Item>
        // ),
      },
      {
        comcode: 'comporiginalnetamount',
        dataIndex: 'comporiginalnetamount',
        width: '10%',
        align: 'right',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'comporiginalnetamount']}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? '')
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'compdiscountrate',
        key: 'compdiscountrate',
        width: '8%',
        align: 'right',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'compdiscountrate']}>
            <Value formatter={(value) => toRate(value)} />
          </Form.Item>
        ),
      },
      {
        comcode: 'compnetamount',
        key: 'compnetamount',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'compnetamount']}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? '')
              }
            />
          </Form.Item>
        ),
        width: '12%',
        align: 'right',
      },
      {
        comcode: 'comptaxcode',
        key: 'taxcode',
        render: (_, _record, index) => (
          <Form.Item name={[fieldName, index, 'taxcode']}>
            <TaxSelect taxflow="INOUT" disabled />
          </Form.Item>
        ),
        width: '8%',
      },
      {
        comcode: 'comptaxrate',
        dataIndex: 'taxrate',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'taxrate']}>
            <Value />
          </Form.Item>
        ),
        width: '7%',
        align: 'right',
      },
      {
        comcode: 'comptaxamount',
        key: 'comptaxamount',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'comptaxamount']}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? '')
              }
            />
          </Form.Item>
        ),
        width: '8%',
        align: 'right',
      },
      {
        comcode: 'compgrossamount',
        key: 'compgrossamount',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'compgrossamount']}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? '')
              }
            />
          </Form.Item>
        ),

        width: '10%',
        align: 'right',
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record) =>
          record.compid === NEW_ASSET_CATALOG_COMPONENT_ID ? (
            <DeleteButton onlyIcon onClick={() => remove(record.compid)} />
          ) : (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => {
                remove(record.compid);
                form.setFieldsValue({
                  assetnetamount: 0,
                  assettaxamount: 0,
                  assetgrossamount: 0,
                  assetmulticomponents:false
                });
              }}
            >
              <DeleteButton onlyIcon />
            </Popconfirm>
          ),

        width: '10%',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remove]
  );

  const title = (
    <>
      <Col span={17}></Col>
      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare([
          'assetnetamount',
          'assettaxamount',
          'assetgrossamount',
          'currcode',
        ])}
      >
        {({ getFieldValue }) => {
          return (
            <Space size={16}>
              <span style={{ color: '#668cff' }}>
                Net:{' '}
                {toCurrency(
                  getFieldValue('assetnetamount'),
                  getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? ''
                )}
              </span>
              <span style={{ color: '#b3c6ff' }}>
                Tax:{' '}
                {toCurrency(
                  getFieldValue('assettaxamount'),
                  getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? ''
                )}
              </span>
              <strong style={{ color: '#668cff' }}>
                Gross:{' '}
                {toCurrency(
                  getFieldValue('assetgrossamount'),
                  getFieldValue('currcode') ?? localStorage.getItem('currcode') ?? ''
                )}
              </strong>
            </Space>
          );
        }}
      </Form.Item>
    </>
  );

  return (
    <DF.Provider pagcode="assetCatalogComponents">
      <Form form={form}>
        <Title level={3} comcode="Components" />
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            return (
              <DataGrid
                title={title}
                columns={columns}
                dataSource={getFieldValue(fieldName)}
                rowKey="compid"
                size="small"
                hideFilters
                extraActions={
                  <ModalControllerNV
                    content={({ setVisible, visible }) =>
                      (!showAsModal || iscreate) && (
                        <AddButton
                          icon={<PlusOutlined />}
                          type="primary"
                          onClick={() => {
                            setVisible(true);
                          }}
                          comcode="add"
                        />
                      )
                    }
                    modal={({ setVisible, visible }) => (
                      <AssetCatalogComponentsTableModal
                        onCancel={() => {
                          setVisible(false);
                        }}
                        onOk={(values) => {
                          addRow(values);
                          setVisible(false);
                        }}
                        visible={visible}
                        currcode={form.getFieldValue('currcode')}
                        formC={form}
                        addorupdate="ADD"
                        catalogid={form.getFieldValue('catalogid')}
                      />
                    )}
                  />
                  // <AddButton
                  //   icon={<PlusOutlined />}
                  //   type="primary"
                  //   onClick={addRow}
                  //   comcode="add"
                  //   //disabled={isNewItemInTable}
                  // />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default AssetCatalogComponentsTable;
